import * as React from 'react';
import { memo } from 'react';
import { Row, useTable, CellProps } from 'react-table';
import { Inbox, Pencil } from 'lucide-react';

import styled from 'styled-components';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from 'atoms';

import { LoadingData, LoadingTable, InfiniteScrollContainer } from 'molecules';

import { IconSizes } from 'components/types';
import { Colors } from 'services/ColorService';
import { getFontFamily } from 'services/FontService';

type Accessors =
  | 'id'
  | 'group'
  | 'startDate'
  | 'numberOfLessons'
  | 'numberOfStudents'
  | 'lessonDays'
  | 'agreementsSum'
  | 'publishedLms'
  | 'publishedLmsForTeacher'
  | 'publishedLmsForMethodist';

export type IRow = {
  [key in Accessors]: React.ReactNode;
} & {
  publishedLms: boolean;
  publishedLmsForTeacher: boolean;
  publishedLmsForMethodist: boolean;
  id: string;
};

export interface IColumn {
  accessor: Accessors;
  flexible?: boolean;
  align?: 'left' | 'right' | 'center';
  Header?: string | React.ReactElement;
  columns?: IColumn[];
  marginRight?: string;
  marginLeft?: string;
  columnWidth?: string;
  Cell?: string | ((props: CellProps<IRow>) => React.ReactElement);
}

export interface IGroupsTableComponent {
  columns: IColumn[];
  rows: IRow[];
  isFlexible: boolean;
  noDataText: string;
  loadMore: () => void;
  hasMore: boolean;
  /** Нет обработчика — нет кнопки «Править» в строке (роль без правки). */
  onEditButtonClick?: (groupId: string) => void;
  /** Row-level click → navigate to the group control center page. */
  onOpenDetail: (groupId: string) => void;
  editButtonLabel?: string;
  loading?: boolean;
}

export const GroupsTableComponent = memo(
  ({
    isFlexible,
    noDataText,
    rows: rowsSource,
    columns,
    loadMore,
    hasMore,
    onEditButtonClick,
    onOpenDetail,
    editButtonLabel,
    loading,
  }: IGroupsTableComponent) => {
    const {
      getTableProps,
      headers,
      getTableBodyProps,
      rows,
      prepareRow,
    } = useTable({
      columns,
      data: rowsSource,
    });

    return (
      <Wrapper>
        <InfiniteScrollContainer
          loadMore={loadMore}
          hasMore={hasMore}
          dataLength={rows.length}
          loading={loading || false}
        >
          <Table {...getTableProps()} flexible={isFlexible} rowShadowOnHover>
            <TableHead>
              <TableRow type="row">
                {headers.map((column: any) => {
                  const {
                    key: cellKey,
                    ...restCellProps
                  } = column.getHeaderProps({
                    type: 'headerCell',
                    flexible: column.flexible && isFlexible,
                    width: column.columnWidth,
                    margin: {
                      right: column.marginRight,
                      left: column.marginLeft,
                    },
                  });

                  return (
                    <TableCell key={cellKey} {...restCellProps}>
                      {column.render('Header')}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody {...getTableBodyProps()}>
              {!rows.length && !loading && (
                <TableRow key="noData">
                  <TableCell type="cell" width="100%" align="center">
                    <NoDataInner>
                      <Inbox size={20} strokeWidth={2} />
                      <span>{noDataText}</span>
                    </NoDataInner>
                  </TableCell>
                </TableRow>
              )}
              {!rows.length && loading && (
                <TableRow>
                  <TableCell width="100%" align="center">
                    <LoadingTable />
                  </TableCell>
                </TableRow>
              )}
              {Boolean(rows.length) &&
                rows.map((row: Row<IRow>) => {
                  prepareRow(row);
                  const { key: rowKey, ...restRowProps } = row.getRowProps();
                  const groupId = row.original.id;

                  return (
                    <GroupsTableRow
                      key={rowKey}
                      type="row"
                      clickable
                      onClick={() => onOpenDetail(groupId)}
                      {...restRowProps}
                    >
                      {row.cells.map((cell: any) => {
                        const {
                          key: cellKey,
                          ...restCellProps
                        } = cell.getCellProps({
                          width: cell.column.columnWidth,
                          flexible: cell.column.flexible && isFlexible,
                          margin: {
                            right: cell.column.marginRight,
                            left: cell.column.marginLeft,
                          },
                        });

                        return (
                          <TableCell key={cellKey} {...restCellProps}>
                            <GroupNameWrapper>
                              {cell.render('Cell')}
                            </GroupNameWrapper>
                          </TableCell>
                        );
                      })}
                      {/* Hover-overlay isolates its clicks from the
                          row-level navigation — keeps the Edit popup
                          opening «in place» without first navigating. */}
                      {onEditButtonClick ? (
                        <RowButtons onClick={(e) => e.stopPropagation()}>
                          <IconButtonStyled
                            size={IconSizes.md}
                            lucideIcon={Pencil}
                            title={editButtonLabel}
                            onClick={() => onEditButtonClick(groupId)}
                          />
                        </RowButtons>
                      ) : null}
                    </GroupsTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </InfiniteScrollContainer>
        {Boolean(rows.length) && loading && <LoadingData />}
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  width: 100%;
  overflow-y: hidden;
  overflow-x: auto;
`;

const GroupNameWrapper = styled.span`
  font-family: ${getFontFamily('medium')};
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.01em;
  color: ${Colors.Black};
`;

const GroupsTableRow = styled((props) => (
  <TableRow {...props}>{props.children}</TableRow>
))`
  position: relative;
`;

const RowButtons = styled.td`
  position: absolute;
  top: 12px;
  right: 24px;
  bottom: 12px;
  display: none;
  align-items: center;
  gap: 8px;
  ${GroupsTableRow}:hover & {
    display: flex;
  }
`;

const IconButtonStyled = styled((props) => <IconButton {...props} />)`
  display: flex;
  height: 32px;
  width: 32px;
  border-radius: 100%;

  &:hover {
    background: ${Colors.Blue};
    & svg {
      color: ${Colors.White};
    }
  }
`;

const NoDataInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${Colors.Gray};
`;
